import { Box, Container, Divider, Paper, Typography } from "@mui/material";
import "./LegalPages.css";

const termsSections = [
  {
    number: "01",
    title: "About Vision X Nexus",
    content: (
      <p>
        Vision X Nexus is a luxury real estate agency based in the UAE providing
        services related to the buying, selling, renting, marketing, and
        promotion of properties across the UAE. Our website provides information
        about our company, services, property opportunities, projects, and ways
        to contact our team.
      </p>
    ),
  },
  {
    number: "02",
    title: "Website Information",
    content: (
      <>
        <p>
          We make reasonable efforts to keep the information on our website
          accurate and up to date.
        </p>

        <p>Property information may change from time to time, including:</p>

        <ul>
          <li>Property prices</li>
          <li>Availability</li>
          <li>Property specifications</li>
          <li>Images and visual materials</li>
          <li>Amenities</li>
          <li>Location information</li>
          <li>Project details</li>
          <li>Completion dates</li>
          <li>Offers and promotions</li>
        </ul>

        <p>
          Information displayed on the website should not be treated as a final
          or guaranteed offer.
        </p>
      </>
    ),
  },
  {
    number: "03",
    title: "Property Availability",
    content: (
      <p>
        Displaying a property or project on our website does not guarantee that
        the property will remain available. Properties may be sold, rented,
        reserved, withdrawn, or otherwise become unavailable without prior
        notice. Users should contact our team to confirm current availability,
        pricing, and other relevant details.
      </p>
    ),
  },
  {
    number: "04",
    title: "Investment Information",
    content: (
      <p>
        Information available on this website should not be interpreted as
        financial, investment, legal, or tax advice. Past performance or general
        market information does not guarantee future returns. Users should
        conduct their own due diligence and obtain independent professional
        advice before making any property or investment decision.
      </p>
    ),
  },
  {
    number: "05",
    title: "Website Use",
    content: (
      <>
        <p>You agree to use this website only for lawful purposes.</p>

        <p>You must not:</p>

        <ul>
          <li>Use the website for fraudulent or unlawful activities</li>
          <li>Attempt to gain unauthorized access to our systems</li>
          <li>Copy or distribute website content without permission</li>
          <li>Misuse enquiry or contact forms</li>
          <li>Upload malicious software or harmful content</li>
          <li>Interfere with the operation or security of the website</li>
        </ul>
      </>
    ),
  },
  {
    number: "06",
    title: "Intellectual Property",
    content: (
      <p>
        Unless otherwise stated, the website, including its text, branding,
        graphics, design, images, logos, and other content, is owned by or used
        with permission by Vision X Nexus. You may not reproduce, modify,
        distribute, publish, or commercially exploit our website content without
        prior written permission.
      </p>
    ),
  },
  {
    number: "07",
    title: "Third-Party Links",
    content: (
      <p>
        Our website may contain links to third-party websites, platforms,
        developers, property portals, social media platforms, or other services.
        These links are provided for convenience or informational purposes.
        Vision X Nexus does not control and is not responsible for the
        availability, accuracy, security, or content of third-party websites.
      </p>
    ),
  },
  {
    number: "08",
    title: "Enquiries & Communication",
    content: (
      <p>
        When you submit an enquiry through our website, you agree that Vision X
        Nexus or an authorized representative may contact you using the contact
        information you provide. This may include communication by phone, email,
        WhatsApp, or other communication channels provided by you.
      </p>
    ),
  },
  {
    number: "09",
    title: "Limitation of Liability",
    content: (
      <p>
        To the extent permitted by applicable law, Vision X Nexus shall not be
        responsible for losses or damages arising from reliance on information
        displayed on the website, changes in property prices or availability,
        temporary website unavailability, third-party websites or services,
        technical errors or interruptions, or decisions made solely on the basis
        of website information.
      </p>
    ),
  },
  {
    number: "10",
    title: "Website Availability",
    content: (
      <p>
        We may modify, suspend, update, or discontinue any part of the website
        or its content at any time without prior notice. We do not guarantee
        that the website will always be available, uninterrupted, or free from
        technical errors.
      </p>
    ),
  },
  {
    number: "11",
    title: "Privacy",
    content: (
      <p>
        Your use of our website is also subject to our Privacy Policy, which
        explains how we collect and handle personal information.
      </p>
    ),
  },
  {
    number: "12",
    title: "Changes to These Terms",
    content: (
      <p>
        We may update these Terms & Conditions from time to time. Changes will
        become effective when the updated version is published on this page. The
        “Last Updated” date will also be revised accordingly.
      </p>
    ),
  },
  {
    number: "13",
    title: "Governing Law",
    content: (
      <p>
        These Terms & Conditions shall be interpreted and applied in accordance
        with the applicable laws and regulations of the United Arab Emirates and
        the relevant jurisdiction applicable to Vision X Nexus and its services.
      </p>
    ),
  },
];

const TermsConditions = () => {
  return (
    <Box className="legal-page">
      {/* HERO */}
      <Box className="legal-hero legal-hero-light">
        <Container maxWidth="lg">
          <Box className="legal-hero-content">
            <Typography className="legal-overline">VISION X NEXUS</Typography>

            <Typography className="legal-main-title">
              Terms & Conditions
            </Typography>

            <Typography className="legal-description">
              Please read these terms carefully before using the Vision X Nexus
              website and services.
            </Typography>

            <Box className="legal-meta">
              <span>Last Updated</span>
              <strong>8 October 2026</strong>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* CONTENT */}
      <Container maxWidth="lg" className="legal-wrapper">
        <Paper className="legal-introduction" elevation={0}>
          <Box className="intro-number">01</Box>

          <Box>
            <Typography className="intro-title">
              Welcome to Vision X Nexus
            </Typography>

            <Typography className="intro-text">
              By accessing or using <strong>visionxnexus.com</strong>, you agree
              to comply with these Terms & Conditions.
            </Typography>

            <Typography className="intro-text">
              If you do not agree with these terms, please do not use this
              website.
            </Typography>
          </Box>
        </Paper>

        <Box className="legal-sections">
          {termsSections.map((section) => (
            <Paper key={section.number} className="legal-card" elevation={0}>
              <Box className="legal-card-number">{section.number}</Box>

              <Box className="legal-card-content">
                <Typography className="legal-card-title">
                  {section.title}
                </Typography>

                <Divider className="legal-divider" />

                <Box className="legal-body">{section.content}</Box>
              </Box>
            </Paper>
          ))}

          {/* CONTACT */}
          <Paper className="legal-contact" elevation={0}>
            <Typography className="legal-contact-overline">
              NEED ASSISTANCE?
            </Typography>

            <Typography className="legal-contact-title">
              Contact Vision X Nexus
            </Typography>

            <Typography className="legal-contact-text">
              For questions regarding these Terms & Conditions, please get in
              touch with our team.
            </Typography>

            <Box className="legal-contact-details">
              <Box>
                <span>Phone</span>
                <strong>+971 54 791 4848</strong>
              </Box>

              <Box>
                <span>Email</span>
                <strong>info@visionxnexus.com</strong>
              </Box>

              <Box>
                <span>Address</span>
                <strong>
                  Bell Rashid Towers, Al Majaz 1, Sharjah, United Arab Emirates
                </strong>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default TermsConditions;
